import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function statusLabel(status) {
  if (status === "sent") return "Sent";
  if (status === "drafted") return "Draft ready";
  return "New";
}

const OfficeInbox = () => {
  const { isSuperUser, isAdmin } = useRole();
  const allowed = isSuperUser || isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");

  const loadStatus = async () => {
    const res = await axiosInstance.get("/inbox/status");
    setStatus(res.data);
    return res.data;
  };

  const loadMessages = async () => {
    const res = await axiosInstance.get("/inbox/messages");
    setMessages(res.data.messages || []);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await loadStatus();
      await loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load the inbox.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    refresh();
  }, [allowed]);

  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (connected === "1") {
      toast.success("Gmail inbox connected.");
      setSearchParams({});
      refresh();
    } else if (error) {
      toast.error(error);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!active) return;
    setDraftSubject(active.draftSubject || "");
    setDraftBody(active.draftBody || "");
  }, [active?.id]);

  const connectGmail = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const res = await axiosInstance.get("/inbox/gmail/connect");
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not start Gmail connect."
      );
    } finally {
      setConnecting(false);
    }
  };

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await axiosInstance.post("/inbox/sync", {}, { timeout: 120000 });
      setStatus((prev) => ({ ...prev, ...res.data }));
      const nextMessages = res.data.messages || [];
      setMessages(nextMessages);
      if (
        activeId &&
        !nextMessages.some((row) => String(row.id) === String(activeId))
      ) {
        setActiveId(null);
        setActive(null);
      }
      const imported = Number(res.data.imported || 0);
      const removed = Number(res.data.removed || 0);
      const parts = [];
      if (imported) parts.push(`imported ${imported} new`);
      if (removed) parts.push(`removed ${removed} deleted/spam`);
      toast.success(
        parts.length
          ? `Sync complete: ${parts.join(", ")}.`
          : "Inbox is up to date."
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not sync Gmail.");
    } finally {
      setSyncing(false);
    }
  };

  const openMessage = async (id) => {
    setActiveId(id);
    try {
      const res = await axiosInstance.get(`/inbox/messages/${id}`);
      setActive(res.data.message);
      setMessages((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, unread: false } : row
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not open the message.");
    }
  };

  const prepareDraft = async () => {
    if (!active?.id || drafting) return;
    setDrafting(true);
    try {
      const res = await axiosInstance.post(
        `/inbox/messages/${active.id}/draft`,
        {},
        { timeout: 120000 }
      );
      setActive(res.data.message);
      setDraftSubject(res.data.message.draftSubject || "");
      setDraftBody(res.data.message.draftBody || "");
      setMessages((prev) =>
        prev.map((row) =>
          row.id === active.id ? { ...row, status: "drafted" } : row
        )
      );
      toast.success("Draft reply is ready. Review it before sending.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not prepare a draft reply."
      );
    } finally {
      setDrafting(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      await axiosInstance.post("/inbox/gmail/disconnect");
      setStatus((prev) => ({
        ...prev,
        connected: false,
        email: "",
        lastSyncAt: null,
      }));
      toast.success("Gmail disconnected.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not disconnect Gmail.");
    }
  };

  const sendReply = async () => {
    if (!active?.id || sending) return;
    if (!draftSubject.trim() || !draftBody.trim()) {
      toast.error("Add a subject and body before sending.");
      return;
    }
    setSending(true);
    try {
      const res = await axiosInstance.post(`/inbox/messages/${active.id}/send`, {
        subject: draftSubject,
        body: draftBody,
      });
      setActive(res.data.message);
      setMessages((prev) =>
        prev.map((row) =>
          row.id === active.id ? { ...row, status: "sent" } : row
        )
      );
      toast.success("Reply sent from the connected Gmail account.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not send the reply.");
    } finally {
      setSending(false);
    }
  };

  if (!allowed) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <p className="p-8 text-gray-600">Only Super Users and Admins can open the inbox.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-blue-900">Office Inbox</h1>
            <p className="text-gray-600 mt-1">
              Connect the office Gmail, review incoming mail, prepare a Claude
              draft, then send it yourself.
            </p>
            {status?.connected ? (
              <p className="text-sm text-gray-700 mt-2">
                Connected as <strong>{status.email}</strong>
                {status.lastSyncAt
                  ? ` · last sync ${formatWhen(status.lastSyncAt)}`
                  : ""}
              </p>
            ) : (
              <p className="text-sm text-gray-700 mt-2">
                Gmail is not connected yet. Use the same Google account that
                receives ABC Realty mail.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!status?.connected ? (
              <button
                type="button"
                onClick={connectGmail}
                disabled={connecting || status?.configured === false}
                className="bg-blue-900 text-white text-sm px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50"
              >
                {connecting ? "Opening Google…" : "Connect Gmail"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={syncNow}
                  disabled={syncing}
                  className="bg-blue-900 text-white text-sm px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50"
                >
                  {syncing ? "Syncing…" : "Sync now"}
                </button>
                <button
                  type="button"
                  onClick={disconnectGmail}
                  className="text-sm px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>

        {status && status.configured === false ? (
          <div className="mb-6 p-4 border border-amber-200 bg-amber-50 rounded-md text-sm text-amber-900">
            Add <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code>,
            and <code>GOOGLE_REDIRECT_URI</code> on the server, then restart.
            In Google Cloud, create an OAuth client and add the redirect
            {` `}
            <code>http://localhost:5001/api/inbox/gmail/callback</code> for
            local use and{" "}
            <code>https://abc.brokercore.app/api/inbox/gmail/callback</code>{" "}
            for the hosted site.
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
              {messages.length === 0 ? (
                <p className="text-sm text-gray-500 p-4">
                  {status?.connected
                    ? "No messages yet. Click Sync now."
                    : "Connect Gmail to load the office inbox."}
                </p>
              ) : (
                messages.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openMessage(row.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      activeId === row.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-sm truncate ${
                          row.unread ? "font-semibold text-gray-900" : "text-gray-800"
                        }`}
                      >
                        {row.from || "(unknown sender)"}
                      </p>
                      <span className="text-[11px] text-gray-500 flex-shrink-0">
                        {statusLabel(row.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 truncate">{row.subject}</p>
                    <p className="text-xs text-gray-500 truncate">{row.snippet}</p>
                  </button>
                ))
              )}
            </div>
            <div className="md:col-span-3 bg-white border border-gray-200 rounded-lg p-4 min-h-[420px]">
              {!active ? (
                <p className="text-sm text-gray-500">Select a message.</p>
              ) : (
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {active.subject}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">From: {active.from}</p>
                  <p className="text-xs text-gray-500">
                    {formatWhen(active.receivedAt)}
                    {active.matchLabel ? ` · ${active.matchLabel}` : ""}
                    {active.intent ? ` · ${active.intent}` : ""}
                  </p>
                  <pre className="mt-4 whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded p-3 max-h-56 overflow-y-auto font-sans">
                    {active.bodyText || active.snippet || "(no body)"}
                  </pre>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={prepareDraft}
                      disabled={drafting || active.status === "sent"}
                      className="bg-blue-900 text-white text-sm px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50"
                    >
                      {drafting ? "Preparing draft…" : "Prepare reply with Claude"}
                    </button>
                    {active.matchedTradeId ? (
                      <a
                        href={`/trade-info?openTrade=${encodeURIComponent(
                          active.matchedTradeId
                        )}`}
                        className="text-sm px-4 py-2 rounded border border-gray-300 text-blue-900 hover:bg-gray-50"
                      >
                        Open trade
                      </a>
                    ) : null}
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Draft subject
                    </label>
                    <input
                      value={draftSubject}
                      onChange={(event) => setDraftSubject(event.target.value)}
                      disabled={active.status === "sent"}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                    <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">
                      Draft body
                    </label>
                    <textarea
                      value={draftBody}
                      onChange={(event) => setDraftBody(event.target.value)}
                      disabled={active.status === "sent"}
                      rows={10}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={sendReply}
                      disabled={sending || active.status === "sent" || !draftBody}
                      className="mt-3 bg-green-700 text-white text-sm px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      {active.status === "sent"
                        ? "Already sent"
                        : sending
                          ? "Sending…"
                          : "Send reply"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeInbox;
